<script lang="ts">
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		Alert,
		Container,
		Text,
		WizardContainer,
		WizardContent,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { dateLabels, digitFormat, startsWeekOnSun } from 'src/helpers/date_helper';
	import { sortOn } from 'src/helpers/utilities';
	import { stringifyLocalAction } from 'src/helpers/actions/local/ui/local_actions_view_helpers';
	//Types
	import type { ScheduleParams } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';

	export let schedule: ScheduleParams = null,
		device: Device = null,
		errors = null,
		activeIndex: number;

	const groupBy = (items: number[] | string[], type = null): string[] => {
		const g = [];
		const inc = type == 'months' ? 1 : 0;
		const labels = dateLabels(type);
		let copyItems = [...items];
		if (type == 'days' && !startsWeekOnSun()) {
			const index = items.indexOf(0 as never);
			if (index >= 0) {
				copyItems[index] = 7;
				labels.push(labels[0]);
			}
		}
		function toGroup(initValue, currentValue, currentIndex: number) {
			initValue = Number(initValue);
			currentValue = Number(currentValue);
			if (currentIndex == 0) {
				g.push({ start: initValue, length: 0 });
				return initValue;
			} else if (currentValue != initValue + 1) {
				g.push({ start: currentValue, length: 0 });
			} else {
				g[g.length - 1].length += 1;
			}
			return currentValue;
		}
		copyItems = sortOn(copyItems);
		copyItems.reduce(toGroup, copyItems[0]);
		return g.map((i) => {
			if (i.length == 0) {
				return type ? `${labels[i.start - inc]}` : `${i.start}`;
			}
			return type
				? `${labels[i.start - inc]}-${labels[i.start + i.length - inc]}`
				: `${i.start}-${i.start + i.length}`;
		});
	};

	const showDaysOfWeek = (days: number[]): string => {
		return groupBy(days, 'days').join(',');
	};

	const showDates = (dates: string[]): string => {
		return groupBy(dates).join(',');
	};

	const showMonths = (months: string[]): string => {
		return groupBy(months, 'months').join(',');
	};

	const showTimes = (schedule: ScheduleParams): string[] => {
		const times: string[] = [];
		let hours: string[] = [schedule.hour.toString()];
		let minutes: string[] = [schedule.min.toString()];
		let seconds: string[] = ['0'];
		if (schedule.type == 'advancedTime') {
			hours =
				schedule.hours.selected && schedule.hours.values.length
					? sortOn([...schedule.hours.values])
					: ['0'];
			minutes =
				schedule.minutes.selected && schedule.minutes.values.length
					? sortOn([...schedule.minutes.values])
					: ['0'];
			seconds =
				schedule.seconds.selected && schedule.seconds.values.length
					? sortOn([...schedule.seconds.values])
					: ['0'];
		}
		seconds.forEach((s) => {
			minutes.forEach((m) => {
				hours.forEach((h) => {
					times.push(`${digitFormat(h)}:${digitFormat(m)}:${digitFormat(s)}`);
				});
			});
		});
		return sortOn(times);
	};
</script>

{#if activeIndex == 3}
	<Container padding={'0'}>
		<Text styleType="h2" class="mb-s"
			>{$format('label_days', {
				default: 'Days',
			})}</Text
		>
		<div class="mt-s"></div>
		{#if errors?.weekday?.show}
			<Alert class="mb-m" heading={errors.weekday.label} type="danger" />
		{:else}
			<Text styleType="controls-small" class="text-bold">
				{$format('schedule_execute_on', {
					default: 'Execute schedule on',
				})}
				<span class="d-inline-block text-primary text-link text-bold"
					>{showDaysOfWeek(schedule.days)}<span class="text-white">;&nbsp</span></span
				>
				{#if schedule.dates.selected && schedule.dates.values.length}
					<span class="d-inline-block text-primary text-link text-bold"
						>{showDates(schedule.dates.values)}<span class="text-white">;&nbsp</span></span
					>
				{/if}
				{#if schedule.months.selected && schedule.months.values.length}
					<span class="d-inline-block text-primary text-link text-bold"
						>{showMonths(schedule.months.values)}<span class="text-white">;&nbsp</span></span
					>
				{/if}
			</Text>
		{/if}
		<hr class="mt-l mb-l" />
		<Text element="span" styleType="h2"
			>{$format('time', {
				default: 'Time',
			})}</Text
		>
		<div class="mt-s"></div>
		<Text styleType="controls-small" class="d-inline-block text-bold"
			>{$format('schedule_execute_at', {
				default: 'Execute schedule at',
			})}</Text
		>
		{#if /^(time|advancedTime)$/.test(schedule.type)}
			{@const times = showTimes(schedule)}
			{#each times as time}
				<span class="d-inline-block controls-small-styles text-bold text-primary"
					>{time}<span class="text-white">;&nbsp</span></span
				>
			{/each}
		{:else}
			{#if schedule.sunHour || schedule.sunMin}
				<span class="d-inline-block controls-small-styles text-bold text-primary"
					>{digitFormat(schedule.sunHour)}:{digitFormat(schedule.sunMin)}
					{$format(schedule.inc == '-' ? 'text_before' : 'text_after', {
						default: schedule.inc == '-' ? 'before' : 'after',
					})}</span
				>
			{/if}
			<span class="d-inline-block controls-small-styles text-bold text-primary"
				>{$format(`schedule_opt_${schedule.type}`, {
					default: schedule.type,
				})}<span class="text-white">;&nbsp</span></span
			>
		{/if}
		<hr class="mt-l mb-l" />
		<Text styleType="h2" class="mb-s"
			>{$format('action', {
				default: 'Action',
			})}</Text
		>
		<div class="mt-s"></div>
		{#each schedule.calls as action, _i}
			<Text element="span" styleType="controls-small" class="text-bold text-primary"
				>{@html stringifyLocalAction(action, device).trim()}<span
					><span class="text-white">;&nbsp</span></span
				></Text
			>
		{:else}
			<Alert class="mb-m" heading={errors.actions.label} type="danger" />
		{/each}
	</Container>
{:else}
	<WizardContainer adaptiveHeight>
		<WizardContent>
			<Container padding={'0'} class="mb-l">
				<Text class="multiple-lines-ellipsis text-bold text-center" styleType="controls-small">
					{#if activeIndex == 0}
						{$format('schedule_execute_on', {
							default: 'Execute schedule on',
						})}
						<span class="d-inline-block text-primary text-link text-bold"
							>{showDaysOfWeek(schedule.days)}<span class="text-white">;&nbsp</span></span
						>
						{#if schedule.dates.selected && schedule.dates.values.length}
							<span class="d-inline-block text-primary text-link text-bold"
								>{showDates(schedule.dates.values)}<span class="text-white">;&nbsp</span
								></span
							>
						{/if}
						{#if schedule.months.selected && schedule.months.values.length}
							<span class="d-inline-block text-primary text-link text-bold"
								>{showMonths(schedule.months.values)}<span class="text-white"
									>;&nbsp</span
								></span
							>
						{/if}
					{:else if activeIndex == 1}
						{$format('schedule_execute_at', {
							default: 'Execute schedule at',
						})}
						{@const times = showTimes(schedule)}
						{#each times as time}
							<span class="d-inline-block text-primary text-link text-bold"
								>{time}<span class="text-white">;&nbsp</span></span
							>
						{/each}
					{/if}
				</Text>
			</Container>
		</WizardContent>
	</WizardContainer>
{/if}

<style>
	:global(.multiple-lines-ellipsis) {
		display: -webkit-box;
		line-clamp: 3;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
</style>
